import {createStore, select, withProps} from '@ngneat/elf';
import {withRequestsCache, withRequestsStatus} from '@ngneat/elf-requests';
import {Injectable} from "@angular/core";
import {AnimalOriginModel} from "../models/animal-origin.model";
import {RestService} from "../../../shared/services/rest.service";
import {Observable, of} from 'rxjs';
import {tap, catchError, map, finalize, shareReplay, filter, take} from "rxjs/operators";
import {AnimalDocumentsRepository} from "./animal-documents.repository";
import {AnimalOriginReasonForTransfer} from "../models/animal-origin-reason-for-transfer.enum";

export interface AnimalOriginState {
  origins: Record<number, AnimalOriginModel[]>;
  pendingReports: AnimalOriginModel[] | null;
}

export const originStore = createStore(
  {name: 'animalOrigin'},
  withProps<AnimalOriginState>({origins: {}, pendingReports: null}),
  withRequestsCache<'animalOrigin'>(),
  withRequestsStatus<'animalOrigin'>()
);

@Injectable({providedIn: 'root'})
export class AnimalOriginRepository {
  private readonly inFlight = new Map<number, Observable<AnimalOriginModel[]>>();

  constructor(
    private readonly _restService: RestService,
    private readonly _documentsRepository: AnimalDocumentsRepository,
  ) {
    // silence is golden
  }

  getByAnimalId(animalId: number, cached: boolean = true): Observable<AnimalOriginModel[]> {
    if (!animalId) {
      return of([]);
    }

    if (!cached) {
      return this._restService.httpGet(`/animal_origins?animal.id=${animalId}`).pipe(
        tap(response => {
          if (!response || !Array.isArray(response)) {
            return;
          }
          originStore.update(state => ({
            ...state,
            origins: {
              ...state.origins,
              [animalId]: response
            }
          }));
        }),
        catchError(() => of([]))
      );
    }

    // synchroner Cache-Check: liegt bereits etwas im Store, kein HTTP
    const cachedOrigins = originStore.getValue().origins[animalId] ?? null;
    if (cachedOrigins !== null) {
      return of(cachedOrigins);
    }

    // läuft bereits ein Request für dieses Tier, dessen geteiltes Observable zurückgeben
    const pending = this.inFlight.get(animalId);
    if (pending) {
      return pending;
    }

    const request$ = this._restService.httpGet(`/animal_origins?animal.id=${animalId}`).pipe(
      tap(response => {
        if (!response || !Array.isArray(response)) {
          return;
        }
        originStore.update(state => ({
          ...state,
          origins: {
            ...state.origins,
            [animalId]: response
          }
        }));
      }),
      map(response => (Array.isArray(response) ? response : [])),
      catchError(() => of([] as AnimalOriginModel[])),
      finalize(() => this.inFlight.delete(animalId)),
      shareReplay({bufferSize: 1, refCount: false})
    );

    this.inFlight.set(animalId, request$);
    return request$;
  }

  getAll() {
    return this._restService.httpGet('/animal_origins?pagination=false')
      .pipe(
        tap((origins: AnimalOriginModel[]) => {
          if (origins && origins.length > 0) {
            originStore.destroy();
            this.setAll(origins);
          }
        })
      );
  }

  loadAllIfEmpty(): Observable<void> {
    const current = originStore.getValue().origins;
    const isEmpty = Object.keys(current).length === 0;

    if (!isEmpty) {
      return of(undefined);
    }

    return this.getAll().pipe(map(() => void 0));
  }

  setAll(animalOrigins: AnimalOriginModel[]) {
    const updatedOrigins = animalOrigins.reduce((acc, entry) => {
      const animalId = entry.animal?.id;
      if (animalId) {
        acc[animalId] = [...(acc[animalId] || []), entry];
      }
      return acc;
    }, {} as Record<number, AnimalOriginModel[]>);

    originStore.update(state => ({
      ...state,
      origins: {
        ...state.origins,
        ...updatedOrigins
      }
    }));
  }

  getPendingReports(): Observable<AnimalOriginModel[]> {
    return this._restService.httpGet('/animal_origins?pendingReport=true&short=true').pipe(
      catchError(() => of([])),
      tap((pendingReports: AnimalOriginModel[]) => {
        originStore.update(state => ({...state, pendingReports}));
      })
    );
  }

  selectPendingReports(): Observable<AnimalOriginModel[]> {
    return originStore.pipe(
      select(state => state.pendingReports),
      filter((pendingReports): pendingReports is AnimalOriginModel[] => !!pendingReports)
    );
  }

  // Lädt die Dashboard-Liste "PDF erstellt & noch nicht versendet" neu, z.B. nach Anlegen oder Versand eines Berichts
  refreshPendingReports(): void {
    this.getPendingReports().pipe(take(1)).subscribe();
  }

  create(payload: any, animalId: number) {
    return this._restService.httpPost('/animal_origins', payload)
      .pipe(
        tap((response: AnimalOriginModel) => {
          originStore.update(state => ({
            ...state,
            origins: {
              ...state.origins,
              [animalId]: [...(state.origins[animalId] || []), response]
            }
          }));
          // Backend erzeugt für diese Typen beim Anlegen eine Bericht-PDF als Dokument
          if (response.type === AnimalOriginReasonForTransfer.FOUND_ANIMAL
            || response.type === AnimalOriginReasonForTransfer.SECURED_BY_POLICE) {
            this._documentsRepository.refreshDocuments(animalId);
            this.refreshPendingReports();
          }
        })
      );
  }

  update(id: number, payload: any, animalId: number) {
    return this._restService.httpPut(`/animal_origins/${id}`, payload)
      .pipe(
        tap((response: AnimalOriginModel) => {
          originStore.update(state => ({
            ...state,
            origins: {
              ...state.origins,
              [animalId]: state.origins[animalId]?.map(entry =>
                entry.id === id ? response : entry
              ) || []
            }
          }));
        })
      );
  }

  clearByAnimalIdAndStateId(animalId: number, stateId: number) {
    originStore.update(state => {
      const current = state.origins[animalId];

      if (!current || !Array.isArray(current)) {
        return state;
      }

      return {
        ...state,
        origins: {
          ...state.origins,
          [animalId]: current.filter(entry => entry.id !== stateId)
        }
      };
    });
  }
}
